// src/pages/Dashboard/sections/NetworkManagement.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getValidSignature, clearSignatureCache } from '../../../services/signatureService';
import EnhancedNetworkGraph from '../../../components/EnhancedNetworkGraph';
import { fetchNetworkData, fetchPointsData, fetchNetworkStabilityFees, searchNodes } from './network-services/networkApiServices';
import { fetchCommissionData } from './network-services/commissionApiService';
import { generateNetworkGraph, findSuperiorNodeId } from './network-services/networkDataUtils';
import NetworkGraphTab from './network-tabs/NetworkGraphTab';
import PointsAnalysisTab from './network-tabs/PointsAnalysisTab';
import CommissionAnalysisTab from './network-tabs/CommissionAnalysisTab';
import FeesTab from './network-tabs/FeesTab';
import SearchTab from './network-tabs/SearchTab';
import '../styles/networkManagement.css';

/**
 * Network Management Section
 * Displays node network visualization, statistics, and management tools
 * Updated to include Commission Analysis tab
 * OPTIMIZED: Reduced unnecessary signature requests and refreshes
 */
const NetworkManagement = ({ 
  wallet, 
  selectedNode, 
  networkStats, 
  onNetworkStatsUpdate, 
  formatNumber,
  formatWalletAddress,
  formatDate
}) => {
  // State for network data and UI
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [activeTab, setActiveTab] = useState('network');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [teamStats, setTeamStats] = useState(null);
  const [pointsData, setPointsData] = useState([]);
  const [feesData, setFeesData] = useState([]);
  const [commissionData, setCommissionData] = useState(null);
  const [pointsPeriod, setPointsPeriod] = useState('30d');
  const [feePeriod, setFeePeriod] = useState('30d');
  const [commissionPeriod, setCommissionPeriod] = useState('30d');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [relationshipLevel, setRelationshipLevel] = useState('all');
  
  // Refs
  const graphContainerRef = useRef(null);
  const prevNodeIdRef = useRef(null);
  const initialTabLoadRef = useRef({
    network: false,
    commissions: false,
    points: false,
    fees: false
  });
  const lastDataFetchTime = useRef({
    network: 0,
    commissions: 0,
    points: 0,
    fees: 0
  });
  
  // Flag to track whether tab change comes from URL or internal state
  const isUrlChangeRef = useRef(false);
  
  // Navigation and search params
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Set active tab from URL if available
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['network', 'commissions', 'points', 'fees', 'search'].includes(tab) && tab !== activeTab) {
      isUrlChangeRef.current = true;
      setActiveTab(tab);
    }
  }, [searchParams]);
  
  // Debounce function to prevent rapid successive API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Debounced fetch functions
  const debouncedFetchCommissionData = useRef(
    debounce((period, page) => {
      handleFetchCommissionData(period, page);
    }, 300)
  ).current;
  
  // OPTIMIZED: Load network data when selected node changes or tab changes
  // Prevents unnecessary refreshes and signature requests
  useEffect(() => {
    if (selectedNode) {
      const currentNodeId = selectedNode.id;
      const prevNodeId = prevNodeIdRef.current;
      const now = Date.now();
      
      // Only trigger data fetch if the node ID has actually changed
      if (prevNodeId !== currentNodeId) {
        console.log(`Node changed from ${prevNodeId} to ${currentNodeId}, fetching new data...`);
        prevNodeIdRef.current = currentNodeId;
        
        // OPTIMIZATION: Don't clear signature cache here, it's handled in the service
        // clearSignatureCache();
        
        // Reset tab load status when node changes
        initialTabLoadRef.current = {
          network: false,
          commissions: false,
          points: false,
          fees: false
        };
        
        // Reset last fetch times
        lastDataFetchTime.current = {
          network: 0,
          commissions: 0,
          points: 0,
          fees: 0
        };
        
        // Load data for the active tab
        if (activeTab === 'network') {
          handleFetchNetworkData();
          initialTabLoadRef.current.network = true;
          lastDataFetchTime.current.network = now;
        } else if (activeTab === 'commissions') {
          handleFetchCommissionData();
          initialTabLoadRef.current.commissions = true;
          lastDataFetchTime.current.commissions = now;
        } else if (activeTab === 'points') {
          handleFetchPointsData();
          initialTabLoadRef.current.points = true;
          lastDataFetchTime.current.points = now;
        } else if (activeTab === 'fees') {
          handleFetchNetworkStabilityFees();
          initialTabLoadRef.current.fees = true;
          lastDataFetchTime.current.fees = now;
        }
      } 
      // Handle tab changes without node changes
      else if (isUrlChangeRef.current || !initialTabLoadRef.current[activeTab]) {
        // Check if we've fetched this data recently (within 60 seconds)
        const shouldFetchNewData = (now - lastDataFetchTime.current[activeTab]) > 60000;
        
        console.log(`Tab changed to ${activeTab}, ${shouldFetchNewData ? 'fetching' : 'using cached'} data...`);
        
        if (activeTab === 'network' && (!initialTabLoadRef.current.network || shouldFetchNewData)) {
          handleFetchNetworkData();
          initialTabLoadRef.current.network = true;
          lastDataFetchTime.current.network = now;
        } else if (activeTab === 'commissions' && (!initialTabLoadRef.current.commissions || shouldFetchNewData)) {
          handleFetchCommissionData();
          initialTabLoadRef.current.commissions = true;
          lastDataFetchTime.current.commissions = now;
        } else if (activeTab === 'points' && (!initialTabLoadRef.current.points || shouldFetchNewData)) {
          handleFetchPointsData();
          initialTabLoadRef.current.points = true;
          lastDataFetchTime.current.points = now;
        } else if (activeTab === 'fees' && (!initialTabLoadRef.current.fees || shouldFetchNewData)) {
          handleFetchNetworkStabilityFees();
          initialTabLoadRef.current.fees = true;
          lastDataFetchTime.current.fees = now;
        }
        
        // Reset the URL change flag
        isUrlChangeRef.current = false;
      }
    }
  }, [selectedNode, activeTab]);
  
  // OPTIMIZED: Reload points data when period changes with debouncing
  useEffect(() => {
    if (selectedNode && activeTab === 'points' && initialTabLoadRef.current.points) {
      // Add a slight delay before fetching to avoid multiple rapid calls
      const timer = setTimeout(() => {
        handleFetchPointsData();
        lastDataFetchTime.current.points = Date.now();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [pointsPeriod]);
  
  // OPTIMIZED: Reload commission data when period changes with debouncing
  useEffect(() => {
    if (selectedNode && activeTab === 'commissions' && initialTabLoadRef.current.commissions) {
      // Use debounced function to prevent multiple rapid calls
      debouncedFetchCommissionData(commissionPeriod);
      lastDataFetchTime.current.commissions = Date.now();
    }
  }, [commissionPeriod]);
  
  // OPTIMIZED: Reload fees data when period changes with debouncing
  useEffect(() => {
    if (selectedNode && activeTab === 'fees' && initialTabLoadRef.current.fees) {
      // Add a slight delay before fetching to avoid multiple rapid calls
      const timer = setTimeout(() => {
        handleFetchNetworkStabilityFees();
        lastDataFetchTime.current.fees = Date.now();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [feePeriod]);
  
  // Handler functions that use imported services
  const handleFetchNetworkData = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoading(true);
    setLoadingMessage('Loading network data...');
    setError('');
    
    try {
      const { teamStatsData, networkData } = await fetchNetworkData(wallet, selectedNode);
      
      // Save team stats
      setTeamStats(teamStatsData);
      
      // Fetch network stability fees information to enrich the node data
      try {
        // Fix: Pass selectedNode instead of '30d' as the second parameter
        const feesData = await fetchNetworkStabilityFees(wallet, selectedNode, '30d');
        
        // Now we have the fees data, process the graph data 
        // Generate graph data from the network result
        const graphData = generateNetworkGraph(networkData, selectedNode, formatWalletAddress, feesData);
        
        // Update parent component with network stats
        if (onNetworkStatsUpdate && teamStatsData) {
          const feesSummary = feesData?.summary || {};
          
          onNetworkStatsUpdate({
            direct_subordinates: teamStatsData.team_statistics?.direct_relationships_count || 0,
            total_network_size: teamStatsData.team_statistics?.total_network_size || 0,
            network_stability_fees: feesSummary.total_fees_received || 0,
            self_referral_fees: feesSummary.self_referral_amount || 0,
            external_fees: feesSummary.external_fees_amount || 0,
            last_updated: feesSummary.last_updated || null
          });
        }
        
        setGraphData(graphData);
      } catch (feesError) {
        console.error('Error fetching network stability fees:', feesError);
        // Continue with basic graph data even if fees data fails
        const graphData = generateNetworkGraph(networkData, selectedNode, formatWalletAddress);
        
        // Update parent component with network stats
        if (onNetworkStatsUpdate && teamStatsData) {
          onNetworkStatsUpdate({
            direct_subordinates: teamStatsData.team_statistics?.direct_relationships_count || 0,
            total_network_size: teamStatsData.team_statistics?.total_network_size || 0,
            network_stability_fees: teamStatsData.node?.total_distributed_points || 0
          });
        }
        
        setGraphData(graphData);
      }
      
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError(`Failed to load network data: ${err.message}`);
      
      // Set fallback empty graph data
      setGraphData({ nodes: [], links: [] });
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  // OPTIMIZED: Reduced unnecessary signature requests
  const handleFetchCommissionData = async (period = commissionPeriod, page = 1) => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoading(true);
    setLoadingMessage('Loading commission data...');
    setError('');
    
    try {
      // Clear commission data first to prevent showing stale data
      setCommissionData(null);
      
      // OPTIMIZATION: No need to force signature clearing here
      // Let the service handle it when necessary
      
      const commissionResult = await fetchCommissionData(
        wallet, 
        period,
        page,
        selectedNode.id
      );
      
      // Set the commission data
      setCommissionData(commissionResult);
      setCurrentPage(page);
      
      // Update parent component with commission totals if available
      if (onNetworkStatsUpdate && commissionResult.summary) {
        onNetworkStatsUpdate({
          direct_subordinates: teamStats?.team_statistics?.direct_relationships_count || 0,
          total_network_size: teamStats?.team_statistics?.total_network_size || 0,
          total_commissions: commissionResult.summary.total_commissions || 0
        });
      }
    } catch (err) {
      console.error('Error fetching commission data:', err);
      setError(`Failed to load commission data: ${err.message}`);
      
      // OPTIMIZATION: Only clear signature cache if there's a specific auth error
      if (err.message && (
        err.message.toLowerCase().includes('signature') || 
        err.message.toLowerCase().includes('auth') ||
        err.message.toLowerCase().includes('token')
      )) {
        console.log('Clearing signature cache due to auth error');
        clearSignatureCache();
      }
      
      // Set fallback empty data
      setCommissionData(null);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  const handleFetchPointsData = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoading(true);
    setLoadingMessage('Loading points data...');
    setError('');
    
    try {
      // Clear points data first to prevent showing stale data
      setPointsData([]);
      
      // OPTIMIZATION: Don't force clear signature cache unnecessarily
      
      const pointsHistoryData = await fetchPointsData(wallet, selectedNode, pointsPeriod);
      setPointsData(pointsHistoryData);
    } catch (err) {
      console.error('Error fetching points data:', err);
      setError(`Failed to load points data: ${err.message}`);
      
      // OPTIMIZATION: Only clear signature cache if there's a specific auth error
      if (err.message && (
        err.message.toLowerCase().includes('signature') || 
        err.message.toLowerCase().includes('auth') ||
        err.message.toLowerCase().includes('token')
      )) {
        clearSignatureCache();
      }
      
      // Set fallback empty data
      setPointsData([]);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  const handleFetchNetworkStabilityFees = async () => {
    if (!selectedNode || !wallet.connected) return;
    
    setIsLoading(true);
    setLoadingMessage(`Loading network stability fees for node ${selectedNode.reference_code}...`);
    setError('');
    
    console.log(`Initiating network stability fees fetch for node: ${selectedNode.id} (${selectedNode.reference_code})`);
    
    try {
      // Clear fees data first to prevent showing stale data
      setFeesData([]);
      
      // OPTIMIZATION: Don't force clear signature cache unnecessarily
      
      const feesData = await fetchNetworkStabilityFees(wallet, selectedNode, feePeriod);
      console.log("Received fees data:", feesData);
      
      // Check if the data is for the correct node
      if (feesData && feesData.node_info && feesData.node_info.reference_code !== selectedNode.reference_code) {
        console.error(`Data mismatch! Requested: ${selectedNode.reference_code}, Received: ${feesData.node_info.reference_code}`);
        setError(`Received data for wrong node (${feesData.node_info.reference_code})`);
      } else {
        setFeesData(feesData);
      }
    } catch (err) {
      console.error('Error fetching network stability fees:', err);
      setError(`Failed to load network stability fees: ${err.message}`);
      
      // OPTIMIZATION: Only clear signature cache if there's a specific auth error
      if (err.message && (
        err.message.toLowerCase().includes('signature') || 
        err.message.toLowerCase().includes('auth') ||
        err.message.toLowerCase().includes('token')
      )) {
        clearSignatureCache();
      }
      
      // Set fallback empty data
      setFeesData([]);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  const handleSearchNodes = async () => {
    if (!selectedNode || !wallet.connected || !searchQuery.trim()) return;
    
    setIsSearching(true);
    setError('');
    
    try {
      const results = await searchNodes(wallet, searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching nodes:', err);
      setError(`Search failed: ${err.message}`);
      
      // Set fallback empty results
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  /**
   * Handle tab change
   * @param {string} tab - The new tab to activate
   */
  const handleTabChange = (tab) => {
    if (tab === activeTab) return; // Don't process if same tab
    
    setActiveTab(tab);
    
    // Update URL search params without triggering reload
    navigate(`/dashboard/network?tab=${tab}`, { replace: true });
  };
  
  /**
   * Handle search form submission
   * @param {Event} e - Form submit event
   */
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearchNodes();
    }
  };
  
  // Render placeholder if wallet not connected
  if (!wallet.connected) {
    return (
      <div className="section-placeholder">
        <p>Please connect your wallet to view your network.</p>
      </div>
    );
  }
  
  // Render placeholder if no node selected
  if (!selectedNode) {
    return (
      <div className="section-placeholder">
        <p>Please select a node to view its network.</p>
      </div>
    );
  }
  
  return (
    <div className="network-management-section">
      <div className="section-header">
        <h2>Network Management</h2>
        <p className="section-subtitle">
          Manage your node network, view statistics, and track performance.
        </p>
      </div>
      
      {/* Network Stats Grid */}
      <div className="network-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{teamStats?.team_statistics?.direct_relationships_count || 0}</div>
            <div className="stat-label">Direct Subordinates</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{teamStats?.team_statistics?.total_network_size || 0}</div>
            <div className="stat-label">Total Network Size</div>
          </div>
        </div>
        
        <div className="stat-card highlight">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">
              {activeTab === 'commissions' && commissionData?.summary 
                ? formatNumber(commissionData.summary.total_commissions || 0, 2)
                : formatNumber(teamStats?.node?.total_distributed_points || 0, 2)}
            </div>
            <div className="stat-label">
              {activeTab === 'commissions' ? 'Total Commissions' : 'Network Stability Fees'}
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 5H19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-details">
            <div className="stat-value">{formatNumber(teamStats?.points_summary?.total_points || 0, 0)}</div>
            <div className="stat-label">Network Points</div>
          </div>
        </div>
      </div>
      
      {/* Network Tabs */}
      <div className="network-tabs">
        <button 
          className={`network-tab-btn ${activeTab === 'network' ? 'active' : ''}`}
          onClick={() => handleTabChange('network')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <circle cx="4" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="20" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="6" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="18" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="6" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="18" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 12H6" stroke="currentColor" strokeWidth="1"/>
            <path d="M18 12H14" stroke="currentColor" strokeWidth="1"/>
            <path d="M9.5 13.5L7 18" stroke="currentColor" strokeWidth="1"/>
            <path d="M14.5 13.5L17 18" stroke="currentColor" strokeWidth="1"/>
            <path d="M9.5 10.5L7 6" stroke="currentColor" strokeWidth="1"/>
            <path d="M14.5 10.5L17 6" stroke="currentColor" strokeWidth="1"/>
          </svg>
          Network Graph
        </button>
        <button 
          className={`network-tab-btn ${activeTab === 'commissions' ? 'active' : ''}`}
          onClick={() => handleTabChange('commissions')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Commission Analysis
        </button>
        <button 
          className={`network-tab-btn ${activeTab === 'points' ? 'active' : ''}`}
          onClick={() => handleTabChange('points')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 17L21 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 21V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3H21V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 3L14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Points Analysis
        </button>
        <button 
          className={`network-tab-btn ${activeTab === 'fees' ? 'active' : ''}`}
          onClick={() => handleTabChange('fees')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Network Stability Fees
        </button>
        <button 
          className={`network-tab-btn ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => handleTabChange('search')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Search Network
        </button>
      </div>
      
      {isLoading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{loadingMessage || 'Loading data...'}</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => {
              if (activeTab === 'network') handleFetchNetworkData();
              else if (activeTab === 'commissions') handleFetchCommissionData();
              else if (activeTab === 'points') handleFetchPointsData();
              else if (activeTab === 'fees') handleFetchNetworkStabilityFees();
              else if (activeTab === 'search' && searchQuery) handleSearchNodes();
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Network Graph Tab */}
          {activeTab === 'network' && (
            <NetworkGraphTab
              graphData={graphData}
              graphContainerRef={graphContainerRef}
              viewMode={viewMode}
              setViewMode={setViewMode}
              relationshipLevel={relationshipLevel}
              setRelationshipLevel={setRelationshipLevel}
              formatWalletAddress={formatWalletAddress}
              formatNumber={formatNumber}
              formatDate={formatDate}
            />
          )}
          
          {/* Commission Analysis Tab */}
          {activeTab === 'commissions' && (
          <CommissionAnalysisTab
            key={`commissions-${selectedNode?.id}`}
            commissionData={commissionData}
            commissionPeriod={commissionPeriod}
            setCommissionPeriod={setCommissionPeriod}
            formatNumber={formatNumber}
            formatWalletAddress={formatWalletAddress}
            formatDate={formatDate}
            onFetchCommissionData={handleFetchCommissionData}
            wallet={wallet}
            selectedNode={selectedNode}
          />
        )}
          
          {/* Points Analysis Tab */}
          {activeTab === 'points' && (
            <PointsAnalysisTab
              key={`points-${selectedNode?.id}`}
              pointsData={pointsData}
              pointsPeriod={pointsPeriod}
              setPointsPeriod={setPointsPeriod}
              formatNumber={formatNumber}
            />
          )}
          
          {/* Network Stability Fees Tab */}
          {activeTab === 'fees' && (
            <FeesTab
              key={`fees-${selectedNode?.id}`}
              feesData={feesData}
              feePeriod={feePeriod}
              setFeePeriod={setFeePeriod}
              formatNumber={formatNumber}
              formatWalletAddress={formatWalletAddress}
              selectedNode={selectedNode}
            />
          )}
          
          {/* Search Network Tab */}
          {activeTab === 'search' && (
            <SearchTab
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchResults={searchResults}
              isSearching={isSearching}
              handleSearch={handleSearch}
              formatWalletAddress={formatWalletAddress}
            />
          )}
        </>
      )}
    </div>
  );
};

export default NetworkManagement;
